/**
 * @graphql cc
 */
import React, { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { expVal } from '@confluence/feature-experiments';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { ReadTimePopupWrapper } from './ReadTimePopupWrapper.tsx';
import { getContentFeatureExtractedEventObjectFromFeatures } from './readTimeUtils.ts';
import { useReadTime, useReadTimeFromFragment } from './hooks/useReadTime.tsx';
import type { useReadTime_content$key } from './hooks/__generated__/useReadTime_content.graphql.ts';

type OriginalReadTimeProps = {
	contentId: string;
	isAbbreviatedReadTime?: boolean;
	isDetailsPanel?: boolean;
	showWordCount?: boolean;
	contentFragmentRef?: useReadTime_content$key | null;
};

type OriginalReadTimeContentProps = Omit<OriginalReadTimeProps, 'contentFragmentRef'> & {
	readTimeData: ReturnType<typeof useReadTime>;
};

const OriginalReadTimeContent = ({
	contentId,
	isAbbreviatedReadTime,
	isDetailsPanel,
	showWordCount,
	readTimeData,
}: OriginalReadTimeContentProps): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		adf,
		loading,
		error,
		contentType,
		readTime,
		wordCount,
		features,
		featuresSkipExpand,
		isReadTimeCapped,
	} = readTimeData;

	useEffect(() => {
		if (createAnalyticsEvent && adf && !loading && !error && contentId && contentType && features) {
			createAnalyticsEvent(
				getContentFeatureExtractedEventObjectFromFeatures({
					contentType,
					contentId,
					features,
					isInEditor: false,
				}),
			).fire();
		}
	}, [contentId, loading, error, contentType, createAnalyticsEvent, adf, features]);

	if (error) {
		return <ErrorDisplay error={error} />;
	}
	if (readTime || (showWordCount && wordCount !== null)) {
		return (
			<ReadTimePopupWrapper
				contentId={contentId}
				isAbbreviatedReadTime={isAbbreviatedReadTime}
				isDetailsPanel={isDetailsPanel}
				showWordCount={showWordCount}
				features={features}
				featuresSkipExpand={featuresSkipExpand}
				wordCount={wordCount}
				readTime={readTime}
				source="originalReadTime"
				isReadTimeCapped={isReadTimeCapped}
			/>
		);
	}

	return null;
};

const OriginalReadTimeWithQuery = ({
	contentId,
	isAbbreviatedReadTime,
	isDetailsPanel,
	showWordCount,
}: Omit<OriginalReadTimeProps, 'contentFragmentRef'>): React.JSX.Element | null => {
	const readTimeData = useReadTime(contentId);

	return (
		<OriginalReadTimeContent
			contentId={contentId}
			isAbbreviatedReadTime={isAbbreviatedReadTime}
			isDetailsPanel={isDetailsPanel}
			showWordCount={showWordCount}
			readTimeData={readTimeData}
		/>
	);
};

const OriginalReadTimeWithFragment = ({
	contentId,
	isAbbreviatedReadTime,
	isDetailsPanel,
	showWordCount,
	contentFragmentRef,
}: Omit<OriginalReadTimeProps, 'contentFragmentRef'> & {
	contentFragmentRef: useReadTime_content$key;
}): React.JSX.Element | null => {
	const readTimeData = useReadTimeFromFragment(contentId, contentFragmentRef);

	return (
		<OriginalReadTimeContent
			contentId={contentId}
			isAbbreviatedReadTime={isAbbreviatedReadTime}
			isDetailsPanel={isDetailsPanel}
			showWordCount={showWordCount}
			readTimeData={readTimeData}
		/>
	);
};

export const OriginalReadTime = ({
	contentFragmentRef,
	...props
}: OriginalReadTimeProps): React.JSX.Element | null => {
	if (
		contentFragmentRef &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_body_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		return <OriginalReadTimeWithFragment {...props} contentFragmentRef={contentFragmentRef} />;
	}

	return <OriginalReadTimeWithQuery {...props} />;
};
